import { useEffect, useRef, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { CheckCircle, Loader2, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { SiteFooter } from '@/components/site-footer'
import { apiV2 } from '@/lib/api-v2-client'

export const Route = createFileRoute('/auth/confirm-email')({
  validateSearch: (search: Record<string, unknown>) => ({ token: (search.token as string) || '' }),
  component: ConfirmEmailPage,
  head: () => ({ meta: [{ title: 'Confirm your email address — Uptrack' }] }),
})

type State = { kind: 'loading' } | { kind: 'done'; email: string } | { kind: 'failed'; message: string }

/** The link in the confirm email. It needs no session: the mailbox is the proof. */
function ConfirmEmailPage() {
  const { token } = Route.useSearch()
  const [state, setState] = useState<State>(() =>
    token ? { kind: 'loading' } : { kind: 'failed', message: 'This link is not valid or has expired.' },
  )
  const started = useRef(false)

  useEffect(() => {
    // One call per link: React may run effects twice in development.
    if (started.current) return
    started.current = true
    if (!token) return
    apiV2
      .confirmAccountEmail(token)
      .then((r) => setState({ kind: 'done', email: r.email }))
      .catch((e: unknown) =>
        setState({ kind: 'failed', message: e instanceof Error ? e.message : 'This link is not valid or has expired.' }),
      )
  }, [token])

  return (
    <div className="flex min-h-screen flex-col">
      <main className="flex flex-1 items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Confirm your email address</CardTitle>
            <CardDescription>Uptrack</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {state.kind === 'loading' && (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Confirming…
              </p>
            )}
            {state.kind === 'done' && (
              <>
                <p role="status" className="flex items-start gap-2 text-sm">
                  <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    <strong>{state.email}</strong> is confirmed. You can now sign in with it.
                  </span>
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button asChild>
                    <Link to="/dashboard/monitors/new">Create a monitor</Link>
                  </Button>
                  <Button asChild variant="outline">
                    <Link to="/dashboard/settings" search={{ tab: 'profile', billing: undefined, plan: undefined }}>
                      Open settings
                    </Link>
                  </Button>
                </div>
              </>
            )}
            {state.kind === 'failed' && (
              <>
                <p role="alert" className="flex items-start gap-2 text-sm font-medium text-destructive">
                  <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{state.message} Ask for a new link in Settings.</span>
                </p>
                <Button asChild variant="outline">
                  <Link to="/dashboard/settings" search={{ tab: 'profile', billing: undefined, plan: undefined }}>
                    Open settings
                  </Link>
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </main>
      <SiteFooter />
    </div>
  )
}
